/**
 * Product form: variant option matching, price updates, Ajax add-to-cart.
 *
 * Expects a root with data-product-form and a JSON script[data-product-json].
 */
(() => {
  const money = (cents) =>
    window.mosesCart?.formatMoney?.(cents) ?? String(cents);

  const parseProduct = (root) => {
    const el = root.querySelector("[data-product-json]");
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  };

  const findVariant = (product, selections) =>
    product.variants.find((variant) =>
      variant.options.every((value, index) => selections[index] === value),
    );

  const syncUrl = (variantId) => {
    if (!variantId || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set("variant", String(variantId));
    window.history.replaceState({}, "", url.toString());
  };

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.product = parseProduct(this);
      if (!this.product) return;

      this.optionInputs = Array.from(
        this.querySelectorAll("[data-option-index]"),
      );
      this.priceEl = this.querySelector("[data-product-price]");
      this.savingsEl = this.querySelector("[data-product-savings]");
      this.savingsAmountEl = this.querySelector(
        "[data-product-savings-amount]",
      );
      this.errorEl = this.querySelector("[data-product-error]");
      this.primaryWrap = this.querySelector("[data-product-form-primary]");
      this.oneTimeWrap = this.querySelector("[data-product-form-onetime]");
      this.primaryForm = this.primaryWrap?.querySelector("form");
      this.oneTimeForm = this.oneTimeWrap?.querySelector("form");
      this.primaryId = this.primaryForm?.querySelector('[name="id"]');
      this.primaryPlan = this.primaryForm?.querySelector(
        '[name="selling_plan"]',
      );
      this.primaryButton = this.primaryForm?.querySelector('[type="submit"]');
      this.primaryLabel = this.primaryButton?.querySelector("[data-cta-label]");
      this.oneTimeId = this.oneTimeForm?.querySelector('[name="id"]');
      this.oneTimeLabel = this.querySelector("[data-buy-once-label]");
      this.ctaLabel = this.dataset.ctaLabel || "Purchase";
      this.subscriptionCtaLabel =
        this.dataset.subscriptionCtaLabel || this.ctaLabel;
      this.soldOutLabel = this.dataset.soldOutLabel || "Sold out";
      this.subscriptionUnavailableLabel = this.dataset.subscriptionUnavailableLabel || "Subscription unavailable";
      this.sellingPlanId = this.dataset.sellingPlanId || "";
      this.buyOnceTemplate = this.dataset.buyOnceTemplate || "";
      this.complimentaryVariantIds = Array.from(
        this.querySelectorAll("[data-complimentary-variant-id]"),
      )
        .map((el) => Number(el.dataset.complimentaryVariantId))
        .filter((id) => id > 0);

      this.optionInputs.forEach((input) => {
        input.addEventListener("change", () => this.onOptionChange());
      });

      this.primaryForm?.addEventListener("submit", (event) =>
        this.onSubmit(event, true),
      );
      this.oneTimeForm?.addEventListener("submit", (event) =>
        this.onSubmit(event, false),
      );

      this.onOptionChange({ updateUrl: false });
    }

    getAllocation(variant) {
      const allocations = variant?.selling_plan_allocations || [];
      return this.sellingPlanId
        ? allocations.find((entry) => String(entry.selling_plan_id) === this.sellingPlanId)
        : allocations[0];
    }

    getSelections() {
      const selections = [];
      this.optionInputs.forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        if (input.matches("select")) {
          selections[index] = input.value;
        } else if (input.matches('input[type="radio"]') && input.checked) {
          selections[index] = input.value;
        }
      });
      return selections;
    }

    onOptionChange({ updateUrl = true } = {}) {
      const selections = this.getSelections();
      const hasOptions = this.optionInputs.length > 0;
      const variant = hasOptions
        ? findVariant(this.product, selections)
        : this.product.variants.find(
            (entry) =>
              entry.id === this.product.selected_or_first_available_variant_id,
          ) || this.product.variants[0];

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.variant = variant;
      if (this.primaryId) this.primaryId.value = variant.id;
      if (this.oneTimeId) this.oneTimeId.value = variant.id;

      const allocation = this.getAllocation(variant);
      const subscriptionUnavailable = Boolean(this.sellingPlanId && !allocation);
      if (this.primaryPlan) {
        if (allocation) {
          this.primaryPlan.value = allocation.selling_plan_id;
          this.primaryPlan.disabled = false;
        } else {
          this.primaryPlan.value = "";
          this.primaryPlan.disabled = true;
        }
      }

      let headline = variant.price;
      let oneTime = variant.price;
      let savings = 0;

      if (allocation) {
        headline = allocation.per_delivery_price ?? allocation.price;
        oneTime = allocation.compare_at_price ?? variant.price;
        savings = Math.max(0, oneTime - allocation.price);
        if (allocation.pair_deliveries === 2) {
          oneTime = variant.price;
          headline = Math.round(allocation.price / allocation.pair_deliveries);
          savings = Math.max(0, oneTime - headline);
        }
      } else if (variant.compare_at_price > variant.price) {
        savings = variant.compare_at_price - variant.price;
      }

      const perRetainer = allocation?.pair_deliveries === 2;
      const displayMoney = (cents) => perRetainer ? money(cents).replace(/([.,])00(?=\D|$)/, "") : money(cents);
      if (this.priceEl) this.priceEl.textContent = `${displayMoney(headline)}${perRetainer ? "/retainer pair" : ""}`;

      if (this.savingsEl && this.savingsAmountEl) {
        if (savings > 0) {
          const template =
            this.savingsEl.dataset.amountOffTemplate || "__AMOUNT__ off";
          this.savingsAmountEl.textContent = template.replace(
            "__AMOUNT__",
            displayMoney(savings),
          );
          this.savingsEl.hidden = false;
          this.savingsEl.style.removeProperty("display");
        } else {
          this.savingsEl.hidden = true;
          this.savingsEl.style.display = "none";
        }
      }

      if (this.oneTimeWrap) {
        this.oneTimeWrap.hidden = !((allocation || this.sellingPlanId) && variant.available);
      }
      if (this.oneTimeLabel && (allocation || this.sellingPlanId)) {
        const price = money(oneTime);
        this.oneTimeLabel.innerHTML = this.buyOnceTemplate.replace(
          "__PRICE__",
          price,
        );
      }

      if (this.primaryButton && this.primaryLabel) {
        this.primaryButton.disabled = !variant.available || subscriptionUnavailable;
        this.primaryLabel.textContent = variant.available
          ? subscriptionUnavailable
            ? this.subscriptionUnavailableLabel
            : allocation
            ? this.subscriptionCtaLabel
            : this.ctaLabel
          : this.soldOutLabel;
      }

      const oneTimeButton = this.oneTimeForm?.querySelector('[type="submit"]');
      if (oneTimeButton) oneTimeButton.disabled = !variant.available;
      if (updateUrl) syncUrl(variant.id);
      this.clearError();
      if (subscriptionUnavailable) this.showError(this.subscriptionUnavailableLabel);
    }

    setUnavailable() {
      if (this.primaryButton && this.primaryLabel) {
        this.primaryButton.disabled = true;
        this.primaryLabel.textContent = this.soldOutLabel;
      }
      if (this.oneTimeWrap) this.oneTimeWrap.hidden = true;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = "";
      this.errorEl.hidden = true;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    async onSubmit(event, withSellingPlan) {
      event.preventDefault();
      if (this.submitting || !this.variant?.available || !window.Alpine?.store)
        return;
      const quantity = Number(
        new FormData(event.currentTarget).get("quantity") || 1,
      );
      if (!Number.isInteger(quantity) || quantity < 1) return;
      const payload = { id: this.variant.id, quantity };
      if (withSellingPlan) {
        const allocation = this.getAllocation(this.variant);
        if (this.sellingPlanId && !allocation) {
          this.showError(this.subscriptionUnavailableLabel);
          return;
        }
        if (allocation) payload.selling_plan = allocation.selling_plan_id;
      }
      this.submitting = true;
      this.clearError();
      const activeButton = event.currentTarget.querySelector('[type="submit"]');
      const activeLabel = withSellingPlan ? this.primaryLabel : this.oneTimeLabel;
      const labelHtml = activeLabel?.innerHTML;
      const buttons = [this.primaryButton, this.oneTimeForm?.querySelector('[type="submit"]')]
        .filter(Boolean)
        .map((button) => ({ button, disabled: button.disabled }));
      buttons.forEach(({ button }) => { button.disabled = true; });
      activeButton?.setAttribute("aria-busy", "true");
      if (activeLabel) activeLabel.textContent = "Adding…";
      try {
        await window.Alpine.store("cart").add(
          payload,
          payload.selling_plan ? this.complimentaryVariantIds : [],
        );
        this.clearError();
      } catch (error) {
        this.showError(error.message || "Unable to add to cart");
      } finally {
        this.submitting = false;
        buttons.forEach(({ button, disabled }) => { button.disabled = disabled; });
        activeButton?.removeAttribute("aria-busy");
        if (activeLabel) activeLabel.innerHTML = labelHtml;
      }
    }
  }

  if (!customElements.get("product-form")) {
    customElements.define("product-form", ProductForm);
  }
})();
